import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Send } from 'lucide-react';
import * as Icons from 'lucide-react';
import { thoughtLeadership, globalRequests } from '../data/mock';
import { RequestDialog } from '../components/RequestDialog';

export const ThoughtLeadership = () => {
  const [isRequestOpen, setIsRequestOpen] = React.useState(false);
  const [activeConfig, setActiveConfig] = React.useState({});
  const [activeContext, setActiveContext] = React.useState("");

  const getIcon = (iconName, size = 32, className = "text-gold") => {
    const Icon = Icons[iconName];
    return Icon ? <Icon size={size} className={className} /> : null;
  };

  const handleTopicClick = (section, topic) => {
    setActiveConfig({
      title: section.popupTitle,
      description: section.popupDescription,
      subject: `${section.popupSubjectPrefix} ${topic}`,
      buttonText: section.buttonText
    });
    setActiveContext(topic);
    setIsRequestOpen(true);
  };


  return (
    <div className="min-h-screen bg-black pt-20">
      <RequestDialog
        isOpen={isRequestOpen}
        onOpenChange={setIsRequestOpen}
        config={activeConfig}
        context={activeContext}
      />

      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-6">
            <span className="text-gold text-sm font-medium uppercase tracking-wide">Knowledge Hub</span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-serif font-bold text-white mb-6">
            Exclusive <span className="text-gold">Insights</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Empowering founders, investors, and corporate leaders with practical legal insights
            and actionable knowledge. Access our curated resources designed to demystify complex
            legal concepts and keep you ahead of the curve.
          </p>
        </div>
      </section>

      {/* Main Content Sections */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {thoughtLeadership.map((section, index) => (
              <div key={section.id}>
                {/* Section Header */}
                <div className="text-center mb-12">
                  <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    {getIcon(section.icon)}
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
                    {section.title}
                  </h2>
                  <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                    {section.description}
                  </p>
                </div>

                {/* Topics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {section.topics.map((topic, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleTopicClick(section, topic)}
                      className="group relative bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-8 hover:border-gold/50 transition-all duration-300 hover:transform hover:scale-[1.02] text-left w-full"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                      <div className="relative z-10">
                        <div className="flex items-start justify-between">
                          <h3 className="text-xl font-bold text-white group-hover:text-gold transition-colors duration-300 pr-8">
                            {topic}
                          </h3>
                          <div className="opacity-0 group-hover:opacity-100 group-hover:scale-110 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300 flex-shrink-0 mt-1">
                            {getIcon(section.topicHoverIcon, 20, "text-gold")}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-br from-gray-900 to-black border border-gold/30 rounded-2xl p-12 text-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent"></div>

            <div className="relative z-10">
              <h2 className="text-4xl font-serif font-bold text-white mb-4">
                Stay <span className="text-gold">Informed</span>
              </h2>

              <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                Subscribe to receive our latest insights, deal playbooks, and legal updates
                directly to your inbox. No spam, just valuable content.
              </p>

              <form className="max-w-md mx-auto">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 px-6 py-3 bg-black/50 border border-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold transition-colors duration-300"
                    required
                  />
                  <button
                    type="submit"
                    className="px-8 py-3 bg-gradient-to-r from-gold to-gold-light text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-gold/30 transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
                  >
                    Subscribe
                  </button>
                </div>
              </form>

              <p className="text-xs text-gray-500 mt-4">
                By subscribing, you agree to receive emails from LEXCEED. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Engagement Section */}
      <section className="py-20 bg-black" hidden>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              Industry <span className="text-gold">Engagement</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Active contribution to the legal and business ecosystem
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icons.Users size={28} className="text-gold" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Charter Member</h3>
              <p className="text-gray-400">
                TiE (The Indus Entrepreneurs)
              </p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icons.Pen size={28} className="text-gold" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Regular Publications</h3>
              <p className="text-gray-400">
                Contributing insights to legal and business communities
              </p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icons.MessageSquare size={28} className="text-gold" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Speaking Engagements</h3>
              <p className="text-gray-400">
                Workshops and seminars for founders and corporates
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
            Need <span className="text-gold">Expert Guidance</span>?
          </h2>

          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            From navigating your first term sheet to structuring complex cross-border deals—
            let's discuss how LEXCEED can provide the clarity and expertise you need.
          </p>

          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-gold to-gold-light text-black font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-gold/40 transform hover:scale-105 transition-all duration-300"
          >
            Schedule a Consultation
            <ArrowRight size={24} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};
